import {ACTION, Action} from "/@lib@/commons/actions.js";
import {IWedEditorMain} from "/@back@/edit/wed/wedEditor.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {IHintsMgr, IItemTypeUiEnv, WspMetaUi} from "/@lib@/wsp/wspMetaUi.js";
import {ActionBtn, Button, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {IEndPoint} from "/@lib@/commons/io/io.js";
import {HintListAnnotMsg, HintsState, IClientHintFamilyModel, IHintFamilyModel, IHintFamilyStates} from "/@lib@/edit/ot/hints.js";
import {House} from "/@lib@/edit/ot/urban.js";
import {IWspXmlHouse} from "/@lib@/wsp/wspsLive.js";
import {SRC} from "/@lib@/wsp/src.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IChainEnv} from "/@lib@/wsp/chain.js";
import {Desk} from "/@lib@/commons/desk.js";

/**
 * Bouton d'accès aux configurations des Hints dans un éditeur.
 * Sert de passerelle pour déclarer le IWedEditorMain auprès du IHintsMgr de l'itemType
 */
export class HintsConfig extends Action<IWedEditorMain> {

	constructor(public hintsMgr: HintsMgr) {
		super('hintsConfig');
		this._label = "Conseils...";
		this._icon = "/@skin@/edit/wed/hints.svg";
	}

	initButtonNode(buttonNode: ActionBtn<IWedEditorMain>, ctx: IWedEditorMain) {
		super.initButtonNode(buttonNode, ctx);
		buttonNode.hidden = true;
		if ((ctx.wedMgr.docHolder.house as IWspXmlHouse).srcFields) {
			//House d'un item reel, ie pas local house par ex.
			//  comme WspsLive.newLocalHouse() : XXX améliorer en filtrant les HintFamilyModels qui sont des IClientHintFamilyModel (donc pas de pendant serveur nécessaire ?)
			this.hintsMgr.getHintFamilyModels(ctx).then((famList: IHintFamilyModel[] | null) => {
				if (famList?.length > 0) {
					buttonNode.hidden = false;
					this.hintsMgr.initForEditor(ctx, buttonNode as ActionBtn<IWedEditorMain>);
				}
			});
		}
	}

	async execute(ctx: IWedEditorMain, ev?: Event) {
		let ct: HTMLElement;
		if (!window.CSS || !(CSS as any).highlights) {
			ct = <c-msg level="warning" style="margin:1em">{Desk.electron ? "Vous devez mettre à jour votre application cliente pour bénéficier de l'affichage des conseils."
				: "Votre navigateur est trop ancien ou ne permet pas d'afficher correctement les conseils."}</c-msg>
		} else {
			const hintsMgr = this.hintsMgr;
			const famModels = await hintsMgr.getHintFamilyModels(ctx);
			if (!famModels) return;
			const hintsSt = hintsMgr.getHintsState(ctx);
			const hintRows = famModels.map((hintModel) => {
				if (!hintModel.isManual) return undefined;
				return this.drawFam(ctx, hintModel, hintsSt, hintsSt?.families?.get(hintModel.key), <div class="famRow"/>);
			})
			ct = <div>
				<ShadowJsx skin="hints-config">
					{hintRows}
				</ShadowJsx>
			</div>;
		}

		if (ev instanceof MouseEvent && ev.pageX > 0 && ev.pageY > 0) {
			POPUP.showMenuFromEvent(ct, ev, ctx);
		} else {
			POPUP.showMenu(ct, {posFrom: ctx, fromX: "middle", fromY: "top", targetX: "middle", targetY: "top"}, ctx);
		}
	}

	drawFam(ctx: IWedEditorMain, hintModel: IHintFamilyModel, hintsSt: HintsState, famSt: IHintFamilyStates | null, div: HTMLElement): HTMLElement {
		const hintsMgr = this.hintsMgr;
		const pending = famSt?.buildPending != null;
		if (pending) famSt.buildPending.then(() => {this.drawFam(ctx, hintModel, hintsSt, famSt, div)});
		div.textContent = null;
		div.append(
			<input id={hintModel.key} type="checkbox" checked={famSt?.shown || undefined}
						 disabled={pending ? "" : undefined}
						 onclick={function (this: HTMLInputElement, ev: Event) {
							 if (this.disabled) return;
							 if (!(famSt?.lastUpdate)) {
								 hintsMgr.buildManualHints(ctx, [hintModel.key]);
								 POPUP.findPopupableParent(this)?.close();
							 } else hintsMgr.setHintsShown(ctx, hintModel.key, this.checked);
						 }}/>,
			<label for={hintModel.key}>{hintModel.label}</label>,
			<Button class="query" î={{label: pending ? "En cours..." : famSt?.lastUpdate ? "Rafraîchir" : "Interroger", uiContext: "dialog"} satisfies OButtonInit}
							disabled={pending ? "" : undefined}
							onclick={function (this: HTMLInputElement, ev: Event) {
								if (this.disabled) return;
								hintsMgr.buildManualHints(ctx, [hintModel.key]);
								POPUP.findPopupableParent(this)?.close();
							}}/>,
		);
		if (!pending && famSt?.lastUpdate) {
			div.append(<span class={famSt.lastFailure ? "lastQuery error" : "lastQuery"}
											 title="Dernière interrogation">{famSt.lastFailure || undefined}{famSt.lastFailure ? " - " : undefined}{new Date(famSt.lastUpdate).toLocaleString()}</span>);
			if (!famSt.lastFailure) {
				if (hintModel.buildOptions) {
					div.append(hintModel.buildOptions(ctx, hintsSt, famSt));
				} else {
					const {enabled, ignored} = hintsSt.getHintStat(famSt.hintModel.key);
					if (ignored === 0 && !famSt.ignoredShown) {
						div.append(<span class="hintsFound" title="Nombre de conseils trouvés">({enabled.toString()})</span>);
					} else {
						let nb = enabled.toString();
						const actsEnabledStr = `${nb} actif(s)`;
						nb = ignored.toString();
						const actsIgnoredStr = `${nb} ignoré(s)`;
						div.append(
							<select onchange={function onShowIgnore(this: HTMLSelectElement, ev: Event) {
								hintsSt.setHintsShown(famSt.hintModel.key, this.value == "enabled");
								hintsSt.setIgnoredHintsShown(famSt.hintModel.key, this.value == "ignored");
								POPUP.findPopupableParent(this)?.close(); //sinon besoin refresh de l'iput checkBox dans ce panneau de getsion des hints.
							}}>
								<option value="enabled" selected={famSt.shown ? "" : undefined}>{actsEnabledStr}</option>
								<option value="ignored" selected={famSt.ignoredShown ? "" : undefined}>{actsIgnoredStr}</option>
							</select>
						);
					}
				}
			}
		}
		if (hintModel.isConfigAvailable) {
			hintModel.isConfigAvailable(ctx.reg).then((isAvail) => {
				if (isAvail) div.append(
					<c-button class="hintModelConfig" î={{title: "Configurer...", icon: "/@skin@/commons/icons/options.svg", uiContext: "bar"} satisfies OButtonInit} onclick={() => hintModel.editConfig(ctx.reg, ctx)}/>
				);
			})
		}

		return div;
	}
}

REG.reg.registerSkin("hints-config", 1, /* language=CSS */ `
	:host {
		padding: .5em;
	}

	.famRow {
		display: flex;
		align-items: center;
		user-select: none;
	}

	label {
		flex: 1;
	}

	.lastQuery {
		font-style: italic;
		font-size: .8em;
		text-align: center;
	}

	.hintsFound {
		margin-inline: .2em;
		color: var(--alt1-color);
		white-space: nowrap;
	}

	.error {
		color: var(--error-color);
	}

	input[type=checkbox]:focus,
	select:focus {
		outline: var(--focus-outline);
	}

	select {
		border: 1px solid var(--border-color);
		margin: .2em;
		border-radius: 3px;
	}

	c-button {
		white-space: nowrap;
		flex: 0 0 auto;
	}
`);

export class HintsMgr implements IHintsMgr {

	protected execFrameUrl: IEndPoint;

	protected configAction: HintsConfig;

	constructor(public wspMetaUi: WspMetaUi) {
		this.execFrameUrl = wspMetaUi.reg.env.universe.httpFrames.web.config.execFrameUrl;

		//XXX msg issus de WebSocket : utilisé uniquement si houseOT master est centralisé sur le serveur.
		//En provenance du svc hints ou du svc de gestion de la house ?
		//if (u.config.wsps.remoteHouseOt) wspMetaUi.reg.env.universe.wsFrames.ws.msgListeners.on("hints", this.onMsg.bind(this));

		//Injection du bouton de configuration dans les éditeurs.
		this.configAction = new HintsConfig(this);
		wspMetaUi.reg.addToList("actions:wed:commonbar:end", "hintsConfig", 1, this.configAction, 500);
	}

	//onMsg(m: JBuildHintsResult) {
	//XXX msg issu de WebSocket : en provenance du svc hints ou du svc de gestion de la house ?
	// if (!m.wspRef) return;
	// const house = this.wspMetaUi.reg.env.universe.wspServer.wspsLive.getHouseIfFetched(m.wspRef);
	// if (house) {
	// 	const srcDt = m.srcDt;
	// 	if (house.srcFields.srcDt === srcDt) {//TODO remonter l'histo des srcDt/prg...
	// 		const msg = House.MSG_FACTORYREG.newFromJson(m) as HintListAnnotMsg;
	// 		msg.prg = house.currentStoredPrg;
	// 		house.masterDoor?.roadEnd.listenIncoming(msg);
	// 	}
	// }
	//}

	initForEditor(editor: IWedEditorMain, manualHintsBtn: ActionBtn<IWedEditorMain>): void {
		manualHintsBtn.accel = ACTION.getAccelText({mainKey: "F4", accelKey: true, shiftKey: true});
		editor.wedMgr.accelKeyMgr.addAccelKey("F4", "accel+shift", this.configAction);
		(editor as IManualHintsBtnHolder).manualHintsBtn = manualHintsBtn;
		//Affichage des hints par défaut...
		// const onInit = (wedMgr: WedMgr) => {
		// 	//Construit par défaut Par famille ?
		// 	if (this.wspMetaUi.reg.getUserData(USERDATA_hintsEnabled, true)) this.showHints(editor);
		// }
		// if (editor.wedMgr.docHolderAsync) onInit(editor.wedMgr); //déjà chargé
		// editor.wedMgr.listeners.on("redrawAtEnd", onInit);
		// //editor.wedMgr.listeners.on("killEditor", (wedMgr) => {});
	}

	async getHintFamilyModels(editor: IWedEditorMain): Promise<IHintFamilyModel[] | null> {
		if (this._HintFamilyModels === undefined) {
			const factories = (editor.wedMgr.reg.env as IItemTypeUiEnv).itemType.reg.getList<() => Promise<IHintFamilyModel>>("wed:hintFamilyModels");
			if (factories) {
				this._HintFamilyModels = (await Promise.all(factories.map<Promise<IHintFamilyModel>>(factory => factory()))).filter((f) => f != null);
			} else {
				this._HintFamilyModels = null;
			}
		}
		return this._HintFamilyModels; // || [new SpellLTConfigHintFM(), new RemoteUrlHintFM()];
	}

	protected _HintFamilyModels?: IHintFamilyModel[]

	async buildManualHints(editor: IWedEditorMain, familyKeys?: string[]) {
		const house = editor.wedMgr.docHolder.house as IWspXmlHouse;
		let hintsSt = house.getStateById<HintsState>(HintsState.type);
		if (!hintsSt || hintsSt.families.size === 0) {
			//création à la volée du hintsState
			const models = await this.getHintFamilyModels(editor);
			if (!models) return;
			if (!hintsSt) {
				hintsSt = new HintsState();
				house.putState(hintsSt);
			}
			for (const m of models) {
				hintsSt.families.set(m.key, {hintModel: m, shown: false, ignoredShown: false, buildPending: undefined, lastUpdate: undefined});
			}
		}
		//Review des familyKeys : existants et pas en cours.
		if (!familyKeys) {
			familyKeys = [];
			for (const f of hintsSt.families.values()) {
				if (f.hintModel.isManual && !f.buildPending) familyKeys.push(f.hintModel.key);
			}
		} else for (let i = 0; i < familyKeys.length; i++) {
			const f = hintsSt.families.get(familyKeys[i]);
			if (!f || !f.hintModel.isManual || f.buildPending) familyKeys.splice(i, 1);
		}
		if (familyKeys.length === 0) return;

		function refreshManualHintsBtn(pr: Promise<any> | undefined) {
			const btn = (editor as IManualHintsBtnHolder).manualHintsBtn;
			if (btn) {
				if (btn.countPending === undefined) btn.countPending = 0;
				btn.countPending += pr ? 1 : -1;
				if (btn.countPending > 0) {
					btn.icon = "/@skin@/edit/wed/hintsPending.svg";
				} else {
					btn.icon = "/@skin@/edit/wed/hints.svg";
				}
			}
		}

		function setPromiseByFamilies(famKeys: string[], pr: Promise<any> | undefined) {
			for (const k of famKeys) hintsSt.families.get(k).buildPending = pr;
			refreshManualHintsBtn(pr);
		}

		function setPromiseByFamily(famKey: string, pr: Promise<any> | undefined) {
			hintsSt.families.get(famKey).buildPending = pr;
			refreshManualHintsBtn(pr);
		}


		function updateHints(msg: HintListAnnotMsg, house: IWspXmlHouse) {
			//Suppression des précédents hints des familles rafraichies.
			const rem: number[] = [];
			hintsSt.scanHints((hint) => {
				if (familyKeys.indexOf(hint.family) >= 0) rem.push(hint.hintId);
			});
			if (rem.length > 0) msg.remAnnots = rem;
			//On force l'affichage des familles de hints retournés
			for (const key of familyKeys) {
				const f = hintsSt.families.get(key);
				if (f) {
					f.shown = true;
					f.ignoredShown = false;
				}
			}
			house.publicDoor.receiveMsg(msg);
			for (const key of familyKeys) {
				const failure = hintsSt.families.get(key)?.lastFailure;
				if (failure) POPUP.showNotif(failure, editor);
			}
		}

		const serverFamKeys = familyKeys.filter((k) => !(hintsSt.families.get(k).hintModel as IClientHintFamilyModel).isClient)

		if (serverFamKeys.length < familyKeys.length) {
			//Hints résolus coté client
			for (const k of familyKeys) {
				const model = hintsSt.families.get(k).hintModel
				if ((model as IClientHintFamilyModel).isClient) {
					setPromiseByFamily(k, (model as IClientHintFamilyModel).buildHints(house).then((hintsMsg) => {
						const h = this.wspMetaUi.reg.env.universe.wspServer.wspsLive.getHouseIfFetched(house.wspRef);
						if (h === house) updateHints(hintsMsg, h);
					}).finally(() => {setPromiseByFamily(k, undefined)}));
				}
			}
		}

		if (serverFamKeys.length > 0) {
			//Hints résolus coté server
			await (editor.wedMgr.reg as IReg<IChainEnv>).env.universe.wspServer.wspsLive.saveAllHouses();
			setPromiseByFamilies(serverFamKeys, this.execFrameUrl.fetchJson<JBuildHintsResult>(`u/hints/BuildHints/${encodeURIComponent(house.wsp.code)}?srcRef=${encodeURIComponent(SRC.srcRef(house.srcFields))}&families=${familyKeys.join(' ')}`, {method: "POST"}).then((hints) => {
				if (hints) {
					const h = this.wspMetaUi.reg.env.universe.wspServer.wspsLive.getHouseIfFetched(house.wspRef);
					if (h === house) {
						const srcDt = hints.srcDt;
						if (h.srcFields.srcDt === srcDt) {//XXX remonter l'histo des srcDt/prg ?
							const msg = House.MSG_FACTORYREG.newFromJson(hints.msg) as HintListAnnotMsg;
							msg.prg = h.currentStoredPrg;
							updateHints(msg, h);
						}
					}
				}
			}).finally(() => {setPromiseByFamilies(serverFamKeys, undefined)}));
		}
	}

	getHintsState(editor: IWedEditorMain): HintsState | null {
		return editor.wedMgr.docHolder.house.getStateById<HintsState>(HintsState.type);
	}

	getFamilyStates(editor: IWedEditorMain): Map<string, IHintFamilyStates> | null {
		return editor.wedMgr.docHolder.house.getStateById<HintsState>(HintsState.type)?.families;
	}

	isAnyHintsShown(editor: IWedEditorMain): boolean {
		const famStates = this.getFamilyStates(editor);
		if (famStates) for (const f of famStates.values()) if (f.shown) return true;
		return false;
	}

	isHintsShown(editor: IWedEditorMain, family: string): boolean {
		return this.getFamilyStates(editor)?.get(family)?.shown || false;
	}

	isSuppressedHintsShown(editor: IWedEditorMain, family: string): boolean {
		return this.getFamilyStates(editor)?.get(family)?.ignoredShown || false;
	}

	setHintsShown(editor: IWedEditorMain, family: string, shown: boolean): void {
		editor.wedMgr.docHolder.house.getStateById<HintsState>(HintsState.type)?.setHintsShown(family, shown);
	}

	setIgnoredHintsShown(editor: IWedEditorMain, family: string, shown: boolean): void {
		editor.wedMgr.docHolder.house.getStateById<HintsState>(HintsState.type)?.setIgnoredHintsShown(family, shown);
	}
}

/**
 * Retour du svc "hints" de cdaction BuildHints.
 * JBuildHintsResult enveloppe un msg JHintsListMsg
 * correspondant à un Msg de type OT qu'on va enrichir
 * avec le MsgOt.prg correspondant à JBuildHintsResult.srcDt
 *
 */
interface JBuildHintsResult {

	/**
	 * Dans une house locale au device permet de retrouver le MsgOt.prg correspondant
	 * à cet état de la source et opérer les ajustements nécessaires.
	 */
	srcDt?: number


	msg: JHintsListMsg
}


/**
 * Schéma json partiel de HintListAnnotMsg (msg OT de la house).
 * Voir class [HintListAnnotMsg]
 */
interface JHintsListMsg {
	type: "hintListAnnot"

	/** Quand utilisé via la svc "hints", le prg est complété locaclement. */
	prg?: number

	metas?: {
		families?: Dict<number>
	}

	remAnnots?: number[]
	msgs: JHintMsg[]
}

interface JHintMsg {
	//...
}

interface IManualHintsBtnHolder extends IWedEditorMain {
	manualHintsBtn?: IManualHintsBtn
}

interface IManualHintsBtn extends ActionBtn<IWedEditorMain> {
	countPending?: number
}
